import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useSizingConfig from "@babackapp/private-api-client/client/use-sizing-config";
import useUpdateSizingConfig from "@babackapp/private-api-client/client/use-update-sizing-config";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { Switch } from "@babackapp/ui/components/switch";
import { TranslatableField } from "@babackapp/ui/components/translatable-field";
import { useCallback, useEffect, useState } from "react";
import { MOCK_CONFIG, USE_MOCK_DATA } from "../lib/mock-data";

export function SizingConfigPanel() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const apiResult = useSizingConfig(shop.id);
  const config = USE_MOCK_DATA ? MOCK_CONFIG : apiResult.data;
  const isLoading = USE_MOCK_DATA ? false : apiResult.isLoading;
  const mutation = useUpdateSizingConfig(shop.id);

  const [threshold, setThreshold] = useState("");
  const [minSampleSize, setMinSampleSize] = useState("");
  const [widgetActive, setWidgetActive] = useState(false);
  const [wordingRunsLarge, setWordingRunsLarge] = useState<
    Record<string, string>
  >({});
  const [wordingRunsSmall, setWordingRunsSmall] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (config) {
      setThreshold(config.threshold?.toString() ?? "");
      setMinSampleSize(config.minSampleSize?.toString() ?? "");
      setWidgetActive(!!config.widgetActivatedAt);
      const wording = config.wording ?? {};
      const runsLarge = wording.runs_large;
      const runsSmall = wording.runs_small;
      setWordingRunsLarge(
        runsLarge && typeof runsLarge === "object"
          ? (runsLarge as Record<string, string>)
          : {},
      );
      setWordingRunsSmall(
        runsSmall && typeof runsSmall === "object"
          ? (runsSmall as Record<string, string>)
          : {},
      );
    }
  }, [config]);

  const locales = shop.preferences?.languages ?? ["en"];
  const defaultLocale = locales[0] ?? "en";

  const handleSave = useCallback(() => {
    mutation.mutate({
      ...(threshold !== "" && { threshold: parseFloat(threshold) }),
      ...(minSampleSize !== "" && {
        minSampleSize: parseInt(minSampleSize, 10),
      }),
      activateWidget: widgetActive,
      wording: {
        runs_large: wordingRunsLarge,
        runs_small: wordingRunsSmall,
      },
    });
  }, [
    mutation,
    threshold,
    minSampleSize,
    widgetActive,
    wordingRunsLarge,
    wordingRunsSmall,
  ]);

  if (isLoading) {
    return (
      <Card className="col-span-4">
        <CardContent className="flex items-center justify-center h-32">
          <div className="animate-pulse text-muted-foreground">
            {t("misc.loading")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-4">
      <CardHeader>
        <CardTitle className="text-md">{t("sizing.config.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          {/* Threshold */}
          <div className="space-y-2">
            <Label htmlFor="threshold">{t("sizing.config.threshold")}</Label>
            <Input
              id="threshold"
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="0.5"
            />
          </div>

          {/* Min sample size */}
          <div className="space-y-2">
            <Label htmlFor="minSampleSize">
              {t("sizing.config.minSampleSize")}
            </Label>
            <Input
              id="minSampleSize"
              type="number"
              min={1}
              step={1}
              value={minSampleSize}
              onChange={(e) => setMinSampleSize(e.target.value)}
              placeholder="15"
            />
          </div>
        </div>

        {/* Widget toggle */}
        <div className="flex items-center gap-3">
          <Switch
            id="activateWidget"
            checked={widgetActive}
            onCheckedChange={setWidgetActive}
          />
          <Label htmlFor="activateWidget">
            {t("sizing.config.activateWidget")}
          </Label>
        </div>

        {/* Wording */}
        <div className="space-y-4">
          <Label>{t("sizing.config.wording")}</Label>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                {t("sizing.config.wordingRunsLarge")}
              </p>
              <TranslatableField
                value={wordingRunsLarge}
                onChange={setWordingRunsLarge}
                locales={locales}
                defaultLocale={defaultLocale}
                placeholder={t("sizing.runsLarge")}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                {t("sizing.config.wordingRunsSmall")}
              </p>
              <TranslatableField
                value={wordingRunsSmall}
                onChange={setWordingRunsSmall}
                locales={locales}
                defaultLocale={defaultLocale}
                placeholder={t("sizing.runsSmall")}
              />
            </div>
          </div>
        </div>

        <Button onClick={handleSave} disabled={mutation.isPending}>
          {mutation.isPending ? t("misc.loading") : t("sizing.config.save")}
        </Button>
      </CardContent>
    </Card>
  );
}
